import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useViewer } from "@/auth/shared";
import { Alert } from "@/components/base/alert";
import { Button } from "@/components/base/button";
import { Checkbox, CheckboxGroup } from "@/components/base/checkbox";
import { Link } from "@/components/base/link";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";

export const Route = createFileRoute("/check-in/$checkInCode")({
  component: CheckInPage,
});

type CheckInOptions = {
  tournamentId: number;
  tournamentDivisionId: number;
  players: { playerProfileId: number; name: string; checkedIn: boolean }[];
};

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <DefaultLayout>
      <div className="mx-auto flex w-full max-w-md flex-col gap-4 pt-12">
        {children}
      </div>
    </DefaultLayout>
  );
}

function Spinner({ label }: { label?: string }) {
  return (
    <DefaultLayout>
      <div className="flex flex-col items-center gap-2 pt-24">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        {label && <p className="text-gray-600">{label}</p>}
      </div>
    </DefaultLayout>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <Centered>
      <Alert color="error" title="Check-in failed" description={message} />
      <Link to="/tournaments">Back to tournaments</Link>
    </Centered>
  );
}

function CheckInPage() {
  const { checkInCode } = Route.useParams();
  const viewer = useViewer();
  const navigate = useNavigate();

  useEffect(() => {
    if (!viewer) {
      navigate({
        to: "/log-in",
        search: { next: `/check-in/${checkInCode}` },
        replace: true,
      });
    }
  }, [viewer, navigate, checkInCode]);

  if (!viewer) {
    return <Spinner />;
  }

  // Key on the code so navigating between check-in URLs remounts and resets
  // per-code state (the auto-fire guard, query, mutation).
  return <CheckInAction key={checkInCode} checkInCode={checkInCode} />;
}

function CheckInAction({ checkInCode }: { checkInCode: string }) {
  const trpc = useTRPC();

  const optionsQuery = useQuery(
    trpc.tournaments.getCheckInOptions.queryOptions({ checkInCode }),
  );
  const checkInMutation = useMutation(
    trpc.tournaments.checkIn.mutationOptions(),
  );

  const options = optionsQuery.data as CheckInOptions | undefined;
  const players = options?.players ?? [];
  const pending = players.filter((p) => !p.checkedIn);
  // Solo zero-tap: the viewer manages exactly one player here. The selector
  // only appears when they manage two or more — keyed on total players, not on
  // how many are still pending, so checking in one of two never auto-fires the
  // other.
  const soloAuto = players.length === 1 && pending.length === 1;

  const { mutate } = checkInMutation;
  const autoFiredRef = useRef(false);
  useEffect(() => {
    if (autoFiredRef.current || !options) return;
    const only = options.players;
    if (only.length === 1 && !only[0].checkedIn) {
      autoFiredRef.current = true;
      mutate({ checkInCode, playerProfileIds: [only[0].playerProfileId] });
    }
  }, [options, checkInCode, mutate]);

  if (checkInMutation.data) {
    return <CheckInResult result={checkInMutation.data} />;
  }
  if (checkInMutation.error) {
    return <ErrorScreen message={checkInMutation.error.message} />;
  }
  if (optionsQuery.isPending || soloAuto) {
    return <Spinner label="Checking you in..." />;
  }
  if (optionsQuery.error) {
    return <ErrorScreen message={optionsQuery.error.message} />;
  }
  if (!options || players.length === 0) {
    return (
      <Centered>
        <Alert
          color="warning"
          title="No one to check in"
          description="None of your players are registered in this division."
        />
        <Link to="/tournaments">Back to tournaments</Link>
      </Centered>
    );
  }

  // Everyone is already checked in — nothing to do but confirm it.
  if (pending.length === 0) {
    return (
      <CheckInResult
        alreadyDone
        result={{
          tournamentId: options.tournamentId,
          players: players.map((p) => ({
            playerProfileId: p.playerProfileId,
            name: p.name,
          })),
        }}
      />
    );
  }

  return (
    <MultiPlayerSelector
      players={players}
      isPending={checkInMutation.isPending}
      onSubmit={(playerProfileIds) => mutate({ checkInCode, playerProfileIds })}
    />
  );
}

function MultiPlayerSelector({
  players,
  isPending,
  onSubmit,
}: {
  players: CheckInOptions["players"];
  isPending: boolean;
  onSubmit: (playerProfileIds: number[]) => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);

  const pending = players.filter((p) => !p.checkedIn);
  const done = players.filter((p) => p.checkedIn);

  const submit = () => {
    // Reconcile against the players still on screen so a stale selection (from
    // a background refetch) can't submit a no-longer-valid id.
    const validIds = new Set(pending.map((p) => p.playerProfileId));
    const ids = selected.map(Number).filter((id) => validIds.has(id));
    if (ids.length > 0) onSubmit(ids);
  };

  return (
    <Centered>
      <div>
        <h1 className="text-lg font-semibold">Who's here?</h1>
        <p className="text-sm text-gray-600">
          Check the box for each of your players who is here, then check them
          in.
        </p>
      </div>
      <CheckboxGroup value={selected} onChange={setSelected} className="gap-2">
        {pending.map((player) => (
          <Checkbox
            key={player.playerProfileId}
            value={String(player.playerProfileId)}
            label={player.name}
          />
        ))}
      </CheckboxGroup>
      {done.length > 0 && (
        <div className="flex flex-col gap-1">
          {done.map((player) => (
            <div
              key={player.playerProfileId}
              className="flex items-center gap-2 text-sm text-gray-500"
            >
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              {player.name} — checked in
            </div>
          ))}
        </div>
      )}
      <Button
        color="primary"
        isDisabled={selected.length === 0 || isPending}
        onPress={submit}
      >
        {isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : selected.length ? (
          `Check in ${selected.length}`
        ) : (
          "Check in"
        )}
      </Button>
    </Centered>
  );
}

function CheckInResult({
  result,
  alreadyDone = false,
}: {
  result: {
    tournamentId: number;
    players: { playerProfileId: number; name: string }[];
  };
  alreadyDone?: boolean;
}) {
  const multiple = result.players.length > 1;
  return (
    <Centered>
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-6 w-6 text-green-600" />
        <h1 className="text-lg font-semibold">
          {alreadyDone
            ? "Already checked in"
            : multiple
              ? "Checked in!"
              : "You're checked in!"}
        </h1>
      </div>
      {multiple && (
        <ul className="flex flex-col gap-1">
          {result.players.map((player) => (
            <li key={player.playerProfileId} className="font-medium">
              {player.name}
            </li>
          ))}
        </ul>
      )}
      <Link
        to="/tournaments/$tournamentId"
        params={{ tournamentId: String(result.tournamentId) }}
      >
        View tournament
      </Link>
    </Centered>
  );
}
